import {
  formatNative,
  type AutomaticExitState,
  type Operation,
  type OperationFailure,
  type QuoteResponse,
} from "@auto-pools/core";
import { shorten, formatToken, formatWhen } from "./format";

/**
 * The automatic-exit states the ENGINE still owns. In these the operator is told what is happening
 * and offered nothing to do, because every action they could take closes the position early — the
 * outcome letting the stop manage it was meant to avoid.
 */
export const ENGINE_OWNED_EXITS: readonly string[] = [
  "armed",
  "retrying",
  "observing",
] satisfies AutomaticExitState[];
/**
 * The states in which the engine has stopped on its own. The manual resume is the remaining path,
 * so these raise a toast whatever the operation's own state is — a `failed` automatic close among
 * them.
 */
export const OPERATOR_OWNED_EXITS: readonly string[] = [
  "exhausted",
  "unauthorized",
  "needs-operator",
] satisfies AutomaticExitState[];
/** What the engine will do next, in the operator's words. Rendered beside the chip for all six. */
export function automaticExitLine(op: Operation): string | null {
  const exit = op.automaticExit;
  if (!exit) return null;
  const count = `${String(exit.rearms)} of ${String(exit.maxRearms)}`;
  switch (exit.state) {
    case "armed":
      return `Stop armed${op.nextAttemptAt ? ` · next eligible retry ${formatWhen(op.nextAttemptAt)}` : ""} · re-arms used ${count}`;
    case "retrying":
      return `Stop is closing this position · re-arms used ${count}`;
    case "observing":
      return `Awaiting the chain outcome · re-arms used ${count}`;
    case "exhausted":
      return `Stop stopped after ${count} automatic attempts · resume it yourself`;
    case "unauthorized":
      // Naming the target is the point: the stop that ordered this close no longer authorizes it,
      // yet a manual resume still sells into the mint the close was approved against.
      // Nullish, not `=== null`: an older or non-server payload omits the field entirely, and an
      // unavailable target is stated by silence rather than by a crash inside render.
      return `Stop no longer authorizes this close${
        exit.targetMint ? ` · a resume still sells into ${shorten(exit.targetMint, 7)}` : ""
      } · re-arms used ${count}`;
    case "needs-operator":
      return `Stop cannot continue on its own · re-arms used ${count}`;
  }
}

export function currentFailure(op: Operation, failure = op.failure) {
  return failure?.step === op.step &&
    failure.intentHash === (op.intentRevision?.activeIntentHash ?? null)
    ? failure
    : null;
}

const explanations: Partial<Record<OperationFailure["code"], [string, string]>> = {
  PRICE_IMPACT: [
    "Swap exceeds your price-impact limit",
    "The swap exceeds the original price-impact limit. Slippage is a different limit and will not fix this.",
  ],
  FEE_CAP: ["Network-fee budget is insufficient", "Approved fee budget cannot cover this step."],
  RENT_CAP: ["Account-rent budget is insufficient", "Approved rent budget cannot cover this step."],
  NATIVE_RESERVE: ["Your SOL reserve must be preserved", "This step would breach your reserve."],
  OWNERSHIP_BUDGET: ["Verified funds are insufficient for this step", "Funds cannot be verified."],
  ZERO_LIQUIDITY: ["The amount cannot create liquidity", "Review amount and range before setup."],
  QUOTE_EXPIRED: [
    "Approval expired before the next step",
    "Completed steps are kept. Approve the same limits again to continue.",
  ],
  QUOTE_UNAVAILABLE: ["Quote unavailable", "The next step could not be calculated."],
  CLEANUP_INELIGIBLE: [
    "Opening cannot be cancelled yet",
    "Empty setup and settled transaction results must be verified first.",
  ],
  POLICY_REVOKED: ["Automatic exit is paused", "The automatic-exit authorization was revoked."],
  RESERVE_CONTENTION: ["Funds are reserved by another operation", "Wait for reserved funds."],
};

type FailureContext = Pick<Operation, "kind" | "step" | "opening" | "retry">;

export function failurePresentation(op: FailureContext, failure: OperationFailure) {
  if (failure.code === "EXIT_SWAP_BLOCKED") {
    const details = failure.exitSwap;
    return [
      "Exit swap blocked; withdrawn proceeds retained",
      details?.reason === "OUTPUT_MINT_MISMATCH"
        ? "The quoted output mint did not match the approved exit target. This swap was not submitted."
        : details?.reason === "PRICE_IMPACT_EXCEEDED"
          ? `Exit price impact exceeded the approved limit.${details.actualImpactBps !== null && details.maxPriceImpactBps !== null ? ` Impact ${details.actualImpactBps} bps; limit ${String(details.maxPriceImpactBps)} bps.` : ""} This swap was not submitted.`
          : "The exit quote did not meet approved conditions. The recorded details do not identify which condition. This swap was not submitted.",
    ];
  }
  if (failure.code === "SIMULATION_FAILED") {
    if (
      failure.simulationReason === "PRICE_SLIPPAGE_OUT_OF_BOUNDS" &&
      op.kind === "open" &&
      op.step === "deposit" &&
      op.opening?.executionVersion === 2
    )
      return [
        "Deposit price is outside your approved bounds",
        "The deposit price was outside your approved bounds. This swap and deposit was not submitted.",
      ];
    if (failure.simulationReason !== "SWAP_OUTPUT_BELOW_MINIMUM")
      return [
        "Transaction simulation did not pass",
        "The recorded details do not identify a specific cause.",
      ];
    return [
      "The swap could not meet your approved minimum",
      `The ${op.kind === "open" ? "funding" : "exit"} swap could not meet your approved minimum. ${op.kind === "open" && op.step === "deposit" && op.opening?.executionVersion === 2 ? "The combined swap and deposit was not submitted." : "This transaction was not submitted."}${op.retry ? ` Approved slippage: ${formatNative(BigInt(op.retry.activeSlippageBps), 2)}%.` : ""}`,
    ];
  }
  if (failure.code === "PRICE_CHANGED") {
    if (failure.priceChanged?.reason === "EXPECTED_OUTPUT_BELOW_APPROVED")
      return [
        "Swap output is below your approved minimum",
        "The current quote would return less than you approved.",
      ];
    if (failure.priceChanged?.reason === "EXACT_INPUT_MISMATCH")
      return [
        "Swap input no longer matches your approval",
        "The current calculation requires a different input.",
      ];
    if (failure.priceChanged?.reason === "PRICE_DRIFT")
      return [
        "Price moved beyond your approved tolerance",
        "The current price is outside the original approved price limit. Increasing slippage does not change this limit.",
      ];
    return ["Price moved beyond your approved limits", "Review actions before continuing."];
  }
  return explanations[failure.code] ?? null;
}

export function operationPresentation(
  op: Operation,
  reportedFailure: OperationFailure | null = op.failure ?? null,
) {
  const failure = currentFailure(op, reportedFailure);
  const opening = op.kind === "open",
    cleanup = op.step === "cleanup";
  const subject = opening ? "Opening" : cleanup ? "Cancellation" : "Closing";
  const label = opening ? "Opening" : cleanup ? "Cancelling" : "Closing";
  const stopped = ["blocked", "failed", "needs-attention"].includes(op.state);
  // `unknown` is the journal's state from the moment bytes are sent until the first observation,
  // so an unsettled operation the scheduler still observes is ordinary progress. Only a recorded
  // reconciliation failure or a stopped operation turns it into an unresolved outcome.
  const active = [
    "queued",
    "preparing",
    "prepared",
    "submitted",
    "unknown",
    "confirmed",
    "provisional",
    "reconciling",
  ].includes(op.state);
  const completedTitle = opening
    ? "Position opened"
    : cleanup
      ? "Opening cancelled"
      : "Position closed";
  const result = (mode: string, tone: string, chip: string, title: string, message: string) => ({
    mode,
    tone,
    chip,
    title,
    message,
    icon:
      mode === "unknown"
        ? "?"
        : (({ red: "×", green: "✓", muted: "…" } as Record<string, string>)[tone] ?? "!"),
    failure,
    stopped,
  });
  if (op.state === "completed")
    return result(
      "completed",
      "green",
      opening ? "Opened" : cleanup ? "Opening cancelled" : "Closed",
      completedTitle,
      "All operation steps have completed.",
    );
  if (op.state === "cancelled")
    return result(
      "retired",
      "muted",
      "Opening retired",
      "Opening retired",
      "This opening has stopped. Check the separate cancellation operation for its result.",
    );
  // OUTCOME_UNKNOWN is also the normal capability reason for unsettled signed work.
  // A refusal is recorded only after the blockhash died and the absence check still could not
  // conclude, so an operation carrying one is no longer ordinary progress even while the journal
  // keeps calling it active — that silence is exactly what left the stalled close looking healthy.
  const unresolved =
    failure?.code === "RECONCILIATION_REQUIRED" ||
    !!op.retry?.absenceRefusal ||
    (stopped &&
      [op.retry?.reasonCode, op.retry?.sameLimits.reasonCode, op.retry?.cancel.reasonCode].includes(
        "OUTCOME_UNKNOWN",
      ));
  if (unresolved)
    return result(
      "unknown",
      "amber",
      `${label} · checking`,
      "Transaction result is not yet known",
      // A refusal means the check cannot conclude on its own, which is the operator's cue to look
      // at the configuration rather than keep waiting. Never replaces the warning about funds.
      `We are checking the original transaction before another action. Funds may already have moved.${
        op.retry?.absenceRefusal ? ` ${op.retry.absenceRefusal}.` : ""
      }`,
    );
  if (active) {
    const waiting =
      !!op.nextAttemptAt &&
      ["queued", "reconciling"].includes(op.state) &&
      (!failure || ["RESERVE_CONTENTION", "UNKNOWN"].includes(failure.code));
    if (waiting)
      return result(
        "waiting",
        "muted",
        `${label} · waiting`,
        failure?.code === "RESERVE_CONTENTION"
          ? "Funds are reserved by another operation"
          : "Checking again shortly",
        "The next check is scheduled automatically. You can leave this page.",
      );
    const title = ["submitted", "unknown", "confirmed", "provisional"].includes(op.state)
      ? "Waiting for network confirmation…"
      : op.state === "reconciling"
        ? `Checking ${opening ? "opening" : "operation"} result…`
        : op.state === "prepared"
          ? "Preparing transaction…"
          : opening
            ? "Opening position…"
            : cleanup
              ? "Cancelling opening…"
              : "Closing position…";
    return result(
      "progress",
      "muted",
      label,
      title,
      op.state === "reconciling"
        ? "Checking the recorded result before the next step. You can leave this page."
        : "Progress will remain available. You can leave this page.",
    );
  }
  // A close can consume its cap successfully and still retain assets.
  if (!opening && !cleanup && op.state === "needs-attention")
    return result(
      "assets",
      "amber",
      "Closed · assets remain",
      "Assets remain after closing",
      "Some assets require separate attention. Recorded residuals are listed below.",
    );
  const exhausted =
    ["blocked", "failed"].includes(op.state) &&
    (failure?.code === "ATTEMPT_CAP" ||
      (op.maxAttempts !== undefined && op.attempts >= op.maxAttempts)) &&
    // The budget counts signed transactions, and a close whose transaction provably never landed
    // spent none of it. Only the server knows that, so for a close its capability decides — the
    // browser must not recompute the budget and disable the very button the stall needs.
    (opening || !op.retry?.sameLimits.allowed);
  if (exhausted)
    return result(
      "exhausted",
      "red",
      `${label} · failed`,
      op.maxAttempts === undefined
        ? "No approved attempts remain"
        : `All ${String(op.maxAttempts)} attempts used`,
      `This ${opening ? "opening" : "operation"} cannot make another attempt.`,
    );
  const stepState =
    op.step === "deposit"
      ? op.opening?.depositState
      : op.step === "funding-swap"
        ? op.opening?.fundingSwapState
        : op.opening?.setupState;
  if (stopped && (stepState === "finalized-error" || (op.state === "failed" && !opening)))
    return result(
      "failure",
      "red",
      `${label} · failed`,
      op.step === "deposit" ? "Deposit transaction failed" : "Transaction failed",
      "This step did not complete. Network fees may have been charged.",
    );
  if (
    op.retry?.reasonCode === "EXECUTOR_UNAVAILABLE" ||
    (failure && ["AUTHORIZATION_BLOCKED", "SIGNING_BLOCKED"].includes(failure.code))
  )
    return result(
      "paused",
      "amber",
      `${label} · paused`,
      `${subject} is paused`,
      "Authorization or signing is required before this step can continue.",
    );
  const explanation = failure ? failurePresentation(op, failure) : null;
  return result(
    "action",
    "amber",
    `${label} · needs action`,
    explanation?.[0] ?? (opening ? "Opening stopped" : `${subject} needs action`),
    explanation?.[1] ?? "Review Details before another action.",
  );
}

/** The aggregators a routed swap can name, as the operator reads them. */
export const ROUTE_PROVIDER_NAMES: Record<"titan" | "jupiter", string> = {
  titan: "Titan",
  jupiter: "Jupiter",
};
/** One provider outcome code in a few words: `http-503` reads as its status alone. */
function routeOutcomeText(outcome: string): string {
  const status = /^http-(\d{3})$/.exec(outcome)?.[1];
  if (status) return status;
  if (outcome.startsWith("unverified")) return "unverified";
  if (outcome.startsWith("prepare-failed")) return "prepare failed";
  return outcome;
}
/**
 * The line under the strip when a provider failed in the operation's latest route quote: the route
 * the server says it chose and who failed. Nothing is inferred from a missing provider: `null`
 * means no route was available, and an unreadable choice names none. A provider that answered but
 * was not better is never listed (the server leaves it out of `routeFailures`).
 */
export function routeFailureLine(op: Pick<Operation, "routeChosen" | "routeFailures">) {
  const failures = op.routeFailures ?? [];
  if (failures.length === 0) return null;
  const list = failures
    .map((f) => `${ROUTE_PROVIDER_NAMES[f.provider]} — ${routeOutcomeText(f.outcome)}`)
    .join(", ");
  const chosen = op.routeChosen;
  if (chosen === "own-pool") return `Via own pool: ${list}`;
  if (chosen === null) return `No route: ${list}`;
  if (chosen === undefined) return `Route failures: ${list}`;
  return `Via ${ROUTE_PROVIDER_NAMES[chosen]} · ${list}`;
}
/**
 * The confirm view's lines for a routed (v3) opening quote: the aggregator and how much more it
 * returns than the own pool, and the automatic recovery the confirmation authorizes. Null for any
 * other quote.
 */
export function routedQuoteLines(
  quote: Pick<QuoteResponse, "route" | "routedRecovery">,
  output: { decimals: number; symbol: string } | undefined,
): { route: string; recovery: string | null } | null {
  const route = quote.route;
  if (!route) return null;
  let advantage: string | null = null;
  try {
    const native = BigInt(route.quotedOut) - BigInt(route.ownPoolOut);
    advantage = output
      ? `${formatToken(formatNative(native, output.decimals), "long")} ${output.symbol}`
      : `${native.toString()} native units`;
  } catch {
    advantage = null;
  }
  const name = ROUTE_PROVIDER_NAMES[route.provider];
  const recovery = quote.routedRecovery;
  return {
    route: advantage === null ? `Swap via ${name}` : `Swap via ${name}, +${advantage} vs the pool`,
    recovery: recovery
      ? `If the deposit fails after the swap, the bot retries ${String(recovery.retries)} times within ${String(Math.round(recovery.windowMs / 60_000))} min, then sells the token back.`
      : null,
  };
}

export function operationSteps(op: Operation, view = operationPresentation(op)) {
  const opening = op.kind === "open",
    sellBack = !opening && !!op.cleanupSellBack,
    cleanup = op.step === "cleanup";
  const routed = opening && op.opening?.executionVersion === 3;
  const separate = !!op.opening?.fundingSwapState;
  const completed = op.state === "completed";
  const assets = view.mode === "assets";
  const done = (value: boolean) => (value ? "finalized-success" : "pending");
  const labels = routed
    ? [
        "Quote approved",
        "Position account created",
        op.routeProvider
          ? `Swap via ${ROUTE_PROVIDER_NAMES[op.routeProvider]}`
          : "Swap via aggregator",
        "Deposit",
        "Monitoring",
      ]
    : opening
      ? [
          "Quote approved",
          separate ? "Funding swap" : "Position account created",
          separate ? "Deposit" : "Swap & deposit",
          "Monitoring",
        ]
      : sellBack
        ? ["Cancel requested", "Close position account", "Sell back", "Return funds", "Done"]
        : cleanup
          ? ["Cancel requested", "Close position account", "Return funds", "Done"]
          : ["Close requested", "Withdraw liquidity", "Return funds", "Done"];
  const raw = routed
    ? [
        done(!!op.intentRevision),
        op.opening?.setupState,
        op.opening?.fundingSwapState,
        op.opening?.depositState,
        done(!!op.opening?.monitoringComplete),
      ]
    : opening
      ? [
          done(!!op.intentRevision),
          separate
            ? op.opening?.fundingSwapState
            : (op.opening?.setupState ??
              (op.opening?.executionVersion === 2 ? "not-required" : "pending")),
          op.opening?.depositState,
          done(!!op.opening?.monitoringComplete),
        ]
      : sellBack
        ? [
            "finalized-success",
            done(completed || assets || op.step === "exit"),
            done(completed || assets),
            done(completed || assets),
            done(completed),
          ]
        : [
            "finalized-success",
            done(completed || assets || (!cleanup && op.step === "exit")),
            done(completed || assets),
            done(completed),
          ];
  const funded =
    op.opening?.monitoringComplete === true || op.opening?.depositState === "finalized-success";
  const index = routed
    ? funded
      ? 4
      : op.step === "deposit"
        ? 3
        : op.step === "funding-swap"
          ? 2
          : 1
    : opening
      ? funded
        ? 3
        : op.step === "deposit"
          ? 2
          : 1
      : sellBack
        ? assets
          ? 4
          : op.step === "exit"
            ? 2
            : 1
        : assets
          ? 3
          : !cleanup && op.step === "exit"
            ? 2
            : 1;
  return labels.map((label, i) => {
    let state = "pending";
    if (raw[i] === "finalized-success") state = "done";
    else if (raw[i] === "not-required") state = "skipped";
    else if (i === index && view.mode === "unknown") state = "current unknown";
    else if (i === index && view.tone === "red") state = "failed exhausted";
    else if (i === index && view.stopped) state = "failed";
    else if (i === index && !["completed", "retired"].includes(view.mode)) state = "current";
    const icons: Record<string, string> = {
      done: "✓",
      skipped: "–",
      "current unknown": "?",
      "failed exhausted": "×",
      failed: "!",
    };
    return { label, state, icon: icons[state] ?? "" };
  });
}

export function recommendsSetup(failure?: OperationFailure | null) {
  return (
    !!failure &&
    (failure.priceChanged?.reason === "PRICE_DRIFT" ||
      [
        "PRICE_IMPACT",
        "FEE_CAP",
        "RENT_CAP",
        "NATIVE_RESERVE",
        "OWNERSHIP_BUDGET",
        "ZERO_LIQUIDITY",
      ].includes(failure.code))
  );
}
